<!--
  Text / Text with icons and links ("Tekst/Tekst z ikonami i linkami")

  Centered header with two columns: hardcoded beaker icon, heading, body text
  and outline link button per column. Max width 1200px. Colours derive from `site_context`.
-->
<template>
  <section class="relative flex w-full flex-col items-center justify-center overflow-x-hidden bg-white px-4 py-12 md:px-16 lg:py-16">
    <div class="mx-auto flex w-full min-w-0 max-w-[1200px] flex-col gap-8 lg:gap-12">
      <div class="flex w-full flex-col gap-1 text-center">
        <p
          v-if="overlineText"
          class="text-[14px] font-semibold uppercase leading-5 tracking-[1px]"
        >
          {{ overlineText }}
        </p>
        <h2
          class="break-words text-[32px] font-medium leading-10 tracking-[-0.64px] lg:text-[36px] lg:leading-[44px] lg:tracking-[-0.72px]"
        >
          {{ headingText }}
        </h2>
      </div>

      <div class="grid w-full grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-8">
        <article
          v-for="column in columns"
          :key="column.key"
          class="flex min-w-0 flex-col gap-3"
        >
          <div
            class="size-12 shrink-0 overflow-hidden"
            :style="{ color: themeColor }"
          >
            <svg
              class="size-full"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M18 8H30V14.5L36.5 32.5C37.3284 34.5 35.8284 36.5 33.5 36.5H14.5C12.1716 36.5 10.6716 34.5 11.5 32.5L18 14.5V8Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M16 8H32"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M20 24H28"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="flex min-w-0 flex-col gap-5">
            <h3
              class="break-words text-[28px] font-medium leading-9 tracking-[-0.56px] lg:text-[32px] lg:leading-10 lg:tracking-[-0.64px]"
              :style="{ color: themeColor }"
            >
              {{ column.title }}
            </h3>

            <p
              v-if="column.text"
              class="whitespace-pre-wrap text-[18px] font-normal leading-7 text-dark"
            >
              {{ column.text }}
            </p>

            <a
              v-if="column.buttonLabel"
              :href="column.buttonUrl || '#'"
              class="inline-flex h-12 w-fit items-center justify-center gap-2 rounded-lg border px-5 py-[14px] text-[16px] font-semibold leading-[18px] transition-opacity hover:opacity-80"
              :style="{ borderColor: buttonAccentColor, color: buttonAccentColor }"
            >
              {{ column.buttonLabel }}
              <svg
                class="size-5 shrink-0"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M7.5 5L12.5 10L7.5 15"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </a>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { resolveSiteContext, themeColorHexFor } from '@/utils/siteThemeColors';

const props = defineProps({
  overlineText: { type: String, default: 'INSTYTUT GENOMIKI I MEDYCYNY MOLEKULARNEJ' },
  headingText: { type: String, default: 'Nasze zasoby i narzędzia' },
  leftTitle: { type: String, default: 'GenoFertility Database' },
  leftText: { type: String, default: '' },
  leftButtonLabel: { type: String, default: 'Dowiedz się więcej' },
  // Stored directly in block data (resource_link); no page-link hydration.
  leftButtonUrl: { type: String, default: '' },
  rightTitle: { type: String, default: 'CMScodeGenome Database' },
  rightText: { type: String, default: '' },
  rightButtonLabel: { type: String, default: 'Dowiedz się więcej' },
  // Stored directly in block data (resource_link); no page-link hydration.
  rightButtonUrl: { type: String, default: '' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null },
});

const themeColor = computed(() => themeColorHexFor(props.site_context));

const buttonAccentColor = computed(() => {
  const site = resolveSiteContext(props.site_context);
  if (site === 'institute') return '#134e4a';
  return themeColorHexFor(props.site_context);
});

const columns = computed(() => [
  {
    key: 'left',
    title: props.leftTitle,
    text: props.leftText,
    buttonLabel: props.leftButtonLabel,
    buttonUrl: props.leftButtonUrl,
  },
  {
    key: 'right',
    title: props.rightTitle,
    text: props.rightText,
    buttonLabel: props.rightButtonLabel,
    buttonUrl: props.rightButtonUrl,
  },
]);
</script>
